import React, { useEffect, useState } from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import logo from "../../assets/logo-aft4.png";
import { calculateDays, calculateFrais, dateFormatter, priceFormatterV3 } from '../../settings/themeSettings';
import dayjs from 'dayjs';

// ─── Palette ────────────────────────────────────────────────────────────────
const PRIMARY   = '#1a3c5e';
const LIGHT_BG  = '#f0f4f8';
const BORDER    = '#c8d4e0';
const WHITE     = '#ffffff';
const GRAY_TEXT = '#555555';

// ─── Styles ─────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: WHITE,
        paddingTop: 20,
        paddingBottom: 36,   // espace pour le pied de page
        paddingHorizontal: 22,
        fontSize: 8,
        fontFamily: 'Helvetica',
    },

    // ── En-tête fixe (répété sur chaque page) ──
    pageHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: PRIMARY,
        paddingBottom: 6,
        marginBottom: 8,
    },
    logo: { width: 70, height: 28 },
    docTitle: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        color: PRIMARY,
        textTransform: 'uppercase',
        letterSpacing: 1,
    },
    refBox: {
        backgroundColor: LIGHT_BG,
        borderRadius: 3,
        paddingHorizontal: 6,
        paddingVertical: 2,
        fontSize: 8,
        color: PRIMARY,
        fontFamily: 'Helvetica-Bold',
    },

    // ── Pied de page fixe ──
    pageFooter: {
        position: 'absolute',
        bottom: 14,
        left: 22,
        right: 22,
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderTopWidth: 0.5,
        borderTopColor: BORDER,
        paddingTop: 4,
        fontSize: 7,
        color: GRAY_TEXT,
    },

    // ── Bloc info dossier ──
    infoGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 8,
        gap: 8,
    },
    infoCol: {
        flex: 1,
        backgroundColor: LIGHT_BG,
        borderRadius: 3,
        padding: 6,
    },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 2,
    },
    infoLabel: { fontSize: 7.5, color: GRAY_TEXT },
    infoValue: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: PRIMARY },

    // ── Section titre ──
    sectionTitle: {
        backgroundColor: PRIMARY,
        color: WHITE,
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        paddingHorizontal: 6,
        paddingVertical: 3,
        marginTop: 8,
        marginBottom: 2,
        borderRadius: 2,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },

    // ── Tableau générique ──
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: LIGHT_BG,
        borderTopWidth: 0.5,
        borderTopColor: BORDER,
        borderBottomWidth: 0.5,
        borderBottomColor: BORDER,
        paddingVertical: 3,
        paddingHorizontal: 4,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: BORDER,
        paddingVertical: 2.5,
        paddingHorizontal: 4,
    },
    tableRowAlt: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: BORDER,
        paddingVertical: 2.5,
        paddingHorizontal: 4,
        backgroundColor: '#f7f9fc',
    },
    tableTotal: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        backgroundColor: LIGHT_BG,
        paddingVertical: 3,
        paddingHorizontal: 4,
        borderTopWidth: 0.5,
        borderTopColor: PRIMARY,
        marginBottom: 4,
    },
    th: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: PRIMARY },
    td: { fontSize: 7.5, color: '#333' },
    tdBold: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: PRIMARY },

    // ── Synthèse comptable ──
    synthRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 2,
        borderBottomWidth: 0.5,
        borderBottomColor: BORDER,
    },
    synthLabel: { fontSize: 7.5, color: GRAY_TEXT },
    synthValue: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: PRIMARY },
});

// ─── Composant principal ─────────────────────────────────────────────────────
const DossierPDF = ({ dossier, showRooming = true, showHeader = true, showComptability = true, clientMode = false, showExcursions = true, showVols = true }) => {

    const [ca, setCa] = useState(0);

    useEffect(() => {
        if (dossier) {
            const globalPax = Number(dossier.pax || 1);
            const calculatedCa = (dossier.factureCircuits || []).reduce((sum, f) => {
                if (f.type === 'AV' || f.type === 'FP') return sum;
                
                const fraisSum = (f.frais || []).reduce((fSum, item) => {
                    if (item.type === 'A' || item.nature === 'A' || item.isCanceled) return fSum;
                    const pu = Number(item.montant || item.prixUnitaire || 0);
                    const qte = Number(item.quantite) || 1;
                    const itemPax = Number(item.nbrPax || item.pax || globalPax);
                    return fSum + (pu * qte * itemPax);
                }, 0);
                
                return sum + fraisSum;
            }, 0);
            
            setCa(calculatedCa);
        }
    }, [dossier]);

    if (!dossier) return null;

    // Helpers locaux
    const fmt  = (v) => priceFormatterV3(v || 0);
    const fmtD = (v) => dateFormatter(v) || '—';

    // Extraction pour plus de clarté
    const hotels = dossier.hotels || [];
    const excursions = dossier.excursions || [];
    const transferts = dossier.transferts || [];
    const locations = dossier.locations || [];

    // Calculs des totaux
    const totalHotels = hotels.reduce((sum, h) => sum + (Number(h.montant) || 0), 0);
    const totalExcursions = excursions.reduce((sum, e) => sum + (Number(e.montant) || 0), 0);
    const totalTransferts = transferts.reduce((sum, t) => sum + (Number(t.montant) || 0), 0);
    const totalLocations = locations.reduce((sum, l) => sum + (Number(l.montant) || 0), 0);
    const totalEnveloppe = (dossier.envelops || []).reduce((s, e) => s + (Number(e.depouillement) || 0), 0);
    const totalDeboursCalculated = totalHotels + totalExcursions + totalTransferts + totalLocations + totalEnveloppe;

    const marge   = ca - totalDeboursCalculated;
    const margePct = ca > 0 ? Math.round(marge / ca * 100) : 0;

    // Composants internes pour éviter les problèmes d'initialisation
    const RenderHeader = () => (
        <View style={S.pageHeader} fixed>
            <Image style={S.logo} source={logo} />
            <Text style={S.docTitle}>DOSSIER {(dossier.type || '').toUpperCase()}</Text>
            <Text style={S.refBox}>{dossier.reference || ''}</Text>
        </View>
    );

    const RenderFooter = () => (
        <View style={S.pageFooter} fixed render={({ pageNumber, totalPages }) => (
            <>
                <Text>Africa For Tourism — {dossier.reference}</Text>
                <Text>Page {pageNumber} / {totalPages}</Text>
            </>
        )} />
    );

    return (
        <Document>
            <Page size="A4" style={S.page}>

                {/* En-tête fixe */}
                <RenderHeader />

                {/* Pied de page fixe */}
                <RenderFooter />

                {/* ── Informations générales ── */}
                <View style={S.infoGrid}>
                    <View style={S.infoCol}>
                        <View style={S.infoRow}><Text style={S.infoLabel}>Référence</Text><Text style={S.infoValue}>{dossier.reference}</Text></View>
                        <View style={S.infoRow}><Text style={S.infoLabel}>Date</Text><Text style={S.infoValue}>{fmtD(dossier.date)}</Text></View>
                        <View style={S.infoRow}><Text style={S.infoLabel}>TO / Client</Text><Text style={S.infoValue}>{dossier.toClient?.libelle || dossier.nomClient || '—'}</Text></View>
                        <View style={S.infoRow}><Text style={S.infoLabel}>Programme</Text><Text style={S.infoValue}>{dossier.programme?.libelle || '—'}</Text></View>
                        <View style={S.infoRow}><Text style={S.infoLabel}>PAX</Text><Text style={S.infoValue}>{dossier.pax}</Text></View>
                        <View style={S.infoRow}><Text style={S.infoLabel}>Nb jours</Text><Text style={S.infoValue}>{dossier.nombreJour || '—'}</Text></View>
                    </View>
                    <View style={S.infoCol}>
                        {!clientMode && (
                            <>
                                <View style={S.infoRow}><Text style={S.infoLabel}>Chauffeur</Text><Text style={S.infoValue}>{dossier.chauffeur?.nomComplet || '—'}</Text></View>
                                <View style={S.infoRow}><Text style={S.infoLabel}>Guide</Text><Text style={S.infoValue}>{dossier.guide?.nomComplet || '—'}</Text></View>
                                <View style={S.infoRow}><Text style={S.infoLabel}>Véhicule</Text><Text style={S.infoValue}>{dossier.vehicule ? `${dossier.vehicule.marque || ''} - ${dossier.vehicule.modele || ''} - ${dossier.vehicule.matricule || ''}`.replace(/^ - | - $/g, '') : '—'}</Text></View>
                            </>
                        )}
                        <View style={S.infoRow}><Text style={S.infoLabel}>Méthode paiement</Text><Text style={S.infoValue}>{dossier.methodePaiement || '—'}</Text></View>
                        {dossier.commentaire ? (
                            <View style={S.infoRow}><Text style={S.infoLabel}>Commentaire</Text><Text style={[S.infoValue, { flex: 1, textAlign: 'right' }]}>{dossier.commentaire}</Text></View>
                        ) : null}
                    </View>
                </View>

                {/* ── Synthèse comptable (Masquée en mode client) ── */}
                {showHeader && !clientMode && (
                    <>
                        <Text style={S.sectionTitle}>Synthèse comptable</Text>
                        <View style={S.tableHeader}>
                            <Text style={[S.th, { width: '20%' }]}>Facture</Text>
                            <Text style={[S.th, { width: '20%' }]}>Chiffre d'affaires</Text>
                            <Text style={[S.th, { width: '20%' }]}>Débours</Text>
                            <Text style={[S.th, { width: '20%' }]}>Marge</Text>
                            <Text style={[S.th, { width: '20%' }]}>%</Text>
                        </View>
                        <View style={S.tableRow}>
                            <Text style={[S.td, { width: '20%' }]}>{dossier.factureCircuits?.find(f => f.typeFacture === 'FC')?.numero || '—'}</Text>
                            <Text style={[S.td, { width: '20%' }]}>{fmt(ca)}</Text>
                            <Text style={[S.td, { width: '20%' }]}>{fmt(totalDeboursCalculated)}</Text>
                            <Text style={[S.td, { width: '20%' }]}>{marge > 0 ? fmt(marge) : '0'}</Text>
                            <Text style={[S.td, { width: '20%' }]}>{margePct}%</Text>
                        </View>
                    </>
                )}

                {/* ── Informations de vol ── */}
                {showVols && (dossier.type === 'circuit' || dossier.type === 'sejour') && dossier.dateVolArrivee && (
                    <>
                        <Text style={S.sectionTitle}>Informations de vol</Text>
                        <View style={S.infoGrid}>
                            <View style={S.infoCol}>
                                <Text style={[S.infoValue, { marginBottom: 3 }]}>VOL ARRIVÉE</Text>
                                <View style={S.infoRow}><Text style={S.infoLabel}>Date</Text><Text style={S.infoValue}>{fmtD(dossier.dateVolArrivee)}</Text></View>
                                <View style={S.infoRow}><Text style={S.infoLabel}>Horaire</Text><Text style={S.infoValue}>{dossier.heureVolArrivee ? new Date(dossier.heureVolArrivee).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '—'}</Text></View>
                                <View style={S.infoRow}><Text style={S.infoLabel}>Compagnie</Text><Text style={S.infoValue}>{dossier.compagnieVolArrivee || '—'}</Text></View>
                                <View style={S.infoRow}><Text style={S.infoLabel}>N° vol</Text><Text style={S.infoValue}>{dossier.numeroVolArrivee || '—'}</Text></View>
                            </View>
                            <View style={S.infoCol}>
                                <Text style={[S.infoValue, { marginBottom: 3 }]}>VOL DÉPART</Text>
                                <View style={S.infoRow}><Text style={S.infoLabel}>Date</Text><Text style={S.infoValue}>{fmtD(dossier.dateVolDepart)}</Text></View>
                                <View style={S.infoRow}><Text style={S.infoLabel}>Horaire</Text><Text style={S.infoValue}>{dossier.heureVolDepart ? new Date(dossier.heureVolDepart).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '—'}</Text></View>
                                <View style={S.infoRow}><Text style={S.infoLabel}>Compagnie</Text><Text style={S.infoValue}>{dossier.compagnieVolDepart || '—'}</Text></View>
                                <View style={S.infoRow}><Text style={S.infoLabel}>N° vol</Text><Text style={S.infoValue}>{dossier.numeroVolDepart || '—'}</Text></View>
                            </View>
                        </View>
                    </>
                )}

                {/* ── Rooming ── */}
                {showRooming && (dossier.rooming?.length > 0) && (
                    <>
                        <Text style={S.sectionTitle}>Rooming ({dossier.rooming.length} pax)</Text>
                        <View style={S.tableHeader}>
                            <Text style={[S.th, { width: '10%' }]}>Civ.</Text>
                            <Text style={[S.th, { width: '45%' }]}>Nom</Text>
                            <Text style={[S.th, { width: '45%' }]}>Prénom</Text>
                        </View>
                        {dossier.rooming.map((p, i) => (
                            <View key={i} style={i % 2 === 0 ? S.tableRow : S.tableRowAlt}>
                                <Text style={[S.td, { width: '10%' }]}>{p.civilite}</Text>
                                <Text style={[S.td, { width: '45%' }]}>{p.nom}</Text>
                                <Text style={[S.td, { width: '45%' }]}>{p.prenom}</Text>
                            </View>
                        ))}
                    </>
                )}

                {/* ── Hébergements ── */}
                {hotels.length > 0 && (
                    <>
                        <Text style={S.sectionTitle}>Hébergements</Text>
                        <View style={S.tableHeader} fixed>
                            <Text style={[S.th, { width: clientMode ? '30%' : '22%' }]}>Du</Text>
                            <Text style={[S.th, { width: clientMode ? '30%' : '22%' }]}>Au</Text>
                            <Text style={[S.th, { width: clientMode ? '8%' : '8%' }]}>Nuits</Text>
                            <Text style={[S.th, { width: clientMode ? '32%' : '22%' }]}>Hôtel</Text>
                            {!clientMode && (
                                <>
                                    <Text style={[S.th, { width: '6%' }]}>PAX</Text>
                                    <Text style={[S.th, { width: '10%' }]}>Régime</Text>
                                    <Text style={[S.th, { width: '10%', textAlign: 'right' }]}>Montant</Text>
                                </>
                            )}
                        </View>
                        {[...hotels].sort((a, b) => new Date(a.dateDebut) - new Date(b.dateDebut))
                            .map((h, i) => (
                                <View key={i} style={i % 2 === 0 ? S.tableRow : S.tableRowAlt}>
                                    <Text style={[S.td, { width: clientMode ? '30%' : '22%' }]}>{fmtD(h.dateDebut)}</Text>
                                    <Text style={[S.td, { width: clientMode ? '30%' : '22%' }]}>{fmtD(h.dateFin)}</Text>
                                    <Text style={[S.td, { width: '8%', textAlign: 'center' }]}>{calculateDays(h.dateDebut, h.dateFin)}</Text>
                                    <Text style={[S.td, { width: clientMode ? '32%' : '22%' }]}>{h.hotel?.nom}</Text>
                                    {!clientMode && (
                                        <>
                                            <Text style={[S.td, { width: '6%', textAlign: 'center' }]}>{h.pax}</Text>
                                            <Text style={[S.td, { width: '10%' }]}>{h.regime}</Text>
                                            <Text style={[S.td, { width: '10%', textAlign: 'right' }]}>{fmt(h.montant)}</Text>
                                        </>
                                    )}
                                </View>
                            ))}
                        {!clientMode && (
                            <View style={S.tableTotal}>
                                <Text style={[S.tdBold, { marginRight: 4 }]}>Total hébergements :</Text>
                                <Text style={S.tdBold}>{fmt(totalHotels)}</Text>
                            </View>
                        )}
                    </>
                )}

                {/* ── Excursions ── */}
                {showExcursions && excursions.length > 0 && (
                    <>
                        <Text style={S.sectionTitle}>Excursions</Text>
                        <View style={S.tableHeader} fixed>
                            <Text style={[S.th, { width: clientMode ? '30%' : '20%' }]}>Date</Text>
                            <Text style={[S.th, { width: clientMode ? '20%' : '10%' }]}>Heure</Text>
                            <Text style={[S.th, { width: clientMode ? '50%' : '35%' }]}>Type</Text>
                            {!clientMode && (
                                <>
                                    <Text style={[S.th, { width: '10%', textAlign: 'center' }]}>PAX</Text>
                                    <Text style={[S.th, { width: '25%', textAlign: 'right' }]}>Montant</Text>
                                </>
                            )}
                        </View>
                        {[...excursions].sort((a, b) => new Date(a.date) - new Date(b.date))
                            .map((e, i) => (
                                <View key={i} style={i % 2 === 0 ? S.tableRow : S.tableRowAlt}>
                                    <Text style={[S.td, { width: clientMode ? '30%' : '20%' }]}>{fmtD(e.date)}</Text>
                                    <Text style={[S.td, { width: clientMode ? '20%' : '10%' }]}>{e.heure || '—'}</Text>
                                    <Text style={[S.td, { width: clientMode ? '50%' : '35%' }]}>{e.type?.libelle}</Text>
                                    {!clientMode && (
                                        <>
                                            <Text style={[S.td, { width: '10%', textAlign: 'center' }]}>{e.pax}</Text>
                                            <Text style={[S.td, { width: '25%', textAlign: 'right' }]}>{fmt(e.montant)}</Text>
                                        </>
                                    )}
                                </View>
                            ))}
                        {!clientMode && (
                            <View style={S.tableTotal}>
                                <Text style={[S.tdBold, { marginRight: 4 }]}>Total excursions :</Text>
                                <Text style={S.tdBold}>{fmt(totalExcursions)}</Text>
                            </View>
                        )}
                    </>
                )}

                {/* ── Transferts ── */}
                {transferts.length > 0 && (
                    <>
                        <Text style={S.sectionTitle}>Transferts</Text>
                        <View style={S.tableHeader} fixed>
                            <Text style={[S.th, { width: clientMode ? '20%' : '18%' }]}>Date</Text>
                            <Text style={[S.th, { width: clientMode ? '10%' : '8%' }]}>Heure</Text>
                            <Text style={[S.th, { width: clientMode ? '35%' : '22%' }]}>Départ</Text>
                            <Text style={[S.th, { width: clientMode ? '35%' : '22%' }]}>Arrivée</Text>
                            {!clientMode && (
                                <>
                                    <Text style={[S.th, { width: '8%', textAlign: 'center' }]}>PAX</Text>
                                    <Text style={[S.th, { width: '22%', textAlign: 'right' }]}>Montant</Text>
                                </>
                            )}
                        </View>
                        {[...transferts].sort((a, b) => new Date(a.date) - new Date(b.date))
                            .map((t, i) => (
                                <View key={i} style={i % 2 === 0 ? S.tableRow : S.tableRowAlt}>
                                    <Text style={[S.td, { width: clientMode ? '20%' : '18%' }]}>{fmtD(t.date)}</Text>
                                    <Text style={[S.td, { width: clientMode ? '10%' : '8%' }]}>{t.heure || '—'}</Text>
                                    <Text style={[S.td, { width: clientMode ? '35%' : '22%' }]}>{t.lieuDepart}</Text>
                                    <Text style={[S.td, { width: clientMode ? '35%' : '22%' }]}>{t.lieuArrivee}</Text>
                                    {!clientMode && (
                                        <>
                                            <Text style={[S.td, { width: '8%', textAlign: 'center' }]}>{t.pax}</Text>
                                            <Text style={[S.td, { width: '22%', textAlign: 'right' }]}>{fmt(t.montant)}</Text>
                                        </>
                                    )}
                                </View>
                            ))}
                        {!clientMode && (
                            <View style={S.tableTotal}>
                                <Text style={[S.tdBold, { marginRight: 4 }]}>Total transferts :</Text>
                                <Text style={S.tdBold}>{fmt(totalTransferts)}</Text>
                            </View>
                        )}
                    </>
                )}

                {/* ── Locations (Masquées en mode client) ── */}
                {!clientMode && locations.length > 0 && (
                    <>
                        <Text style={S.sectionTitle}>Locations</Text>
                        <View style={S.tableHeader} fixed>
                            <Text style={[S.th, { width: '25%' }]}>Date début</Text>
                            <Text style={[S.th, { width: '25%' }]}>Date fin</Text>
                            <Text style={[S.th, { width: '15%', textAlign: 'center' }]}>Jours</Text>
                            <Text style={[S.th, { width: '35%', textAlign: 'right' }]}>Montant</Text>
                        </View>
                        {[...locations].sort((a, b) => new Date(a.dateDebut) - new Date(b.dateDebut))
                            .map((l, i) => (
                                <View key={i} style={i % 2 === 0 ? S.tableRow : S.tableRowAlt}>
                                    <Text style={[S.td, { width: '25%' }]}>{fmtD(l.dateDebut)}</Text>
                                    <Text style={[S.td, { width: '25%' }]}>{fmtD(l.dateFin)}</Text>
                                    <Text style={[S.td, { width: '15%', textAlign: 'center' }]}>{l.nombreJour}</Text>
                                    <Text style={[S.td, { width: '35%', textAlign: 'right' }]}>{fmt(l.montant)}</Text>
                                </View>
                            ))}
                        <View style={S.tableTotal}>
                            <Text style={[S.tdBold, { marginRight: 4 }]}>Total locations :</Text>
                            <Text style={S.tdBold}>{fmt(totalLocations)}</Text>
                        </View>
                    </>
                )}

                {/* ── Total Public (En mode client uniquement) ── */}
                {clientMode && (
                    <View style={[S.tableTotal, { marginTop: 20 }]}>
                        <Text style={[S.tdBold, { fontSize: 10 }]}>TOTAL DU SERVICE : </Text>
                        <Text style={[S.tdBold, { fontSize: 10, marginLeft: 10 }]}>{fmt(ca)}</Text>
                    </View>
                )}

                {/* ── Documents comptables ── */}
                {showComptability && !clientMode && (
                    <>
                        <Text style={S.sectionTitle}>Documents comptables</Text>
                        <View style={{ marginTop: 4 }}>
                            <View style={S.synthRow}>
                                <Text style={S.synthLabel}>Enveloppe (total réel)</Text>
                                <Text style={S.synthValue}>
                                    {fmt((dossier.envelops || []).reduce((s, e) => s + (e.depouillement || 0), 0))}
                                </Text>
                            </View>
                            {/* ── Détail des débours ── */}
                            {totalHotels > 0 && (
                                <View style={S.synthRow}>
                                    <Text style={S.synthLabel}>HEBERGEMENT</Text>
                                    <Text style={S.synthValue}>{fmt(totalHotels)}</Text>
                                </View>
                            )}
                            {totalExcursions > 0 && (
                                <View style={S.synthRow}>
                                    <Text style={S.synthLabel}>EXCURSION</Text>
                                    <Text style={S.synthValue}>{fmt(totalExcursions)}</Text>
                                </View>
                            )}
                            {totalTransferts > 0 && (
                                <View style={S.synthRow}>
                                    <Text style={S.synthLabel}>TRANSFERT</Text>
                                    <Text style={S.synthValue}>{fmt(totalTransferts)}</Text>
                                </View>
                            )}
                            {totalLocations > 0 && (
                                <View style={S.synthRow}>
                                    <Text style={S.synthLabel}>LOCATION</Text>
                                    <Text style={S.synthValue}>{fmt(totalLocations)}</Text>
                                </View>
                            )}
                            
                            {/* Affichage des autres frais non gérés par les tableaux s'ils existent */}
                            {(dossier.frais || []).filter(f => 
                                f.montant > 0 && 
                                !['HEBERGEMENT', 'EXCURSION', 'TRANSFERT', 'LOCATION'].includes(f.frais?.toUpperCase())
                            ).map((f, i) => (
                                <View key={i} style={S.synthRow}>
                                    <Text style={S.synthLabel}>{f.frais}</Text>
                                    <Text style={S.synthValue}>{fmt(f.montant)}</Text>
                                </View>
                            ))}

                            <View style={[S.synthRow, { backgroundColor: LIGHT_BG, marginTop: 4 }]}>
                                <Text style={[S.synthLabel, { color: PRIMARY, fontFamily: 'Helvetica-Bold' }]}>Total débours</Text>
                                <Text style={[S.synthValue, { color: PRIMARY, fontFamily: 'Helvetica-Bold' }]}>{fmt(totalDeboursCalculated)}</Text>
                            </View>
                            <View style={[S.synthRow, { borderBottomWidth: 0 }]}>
                                <Text style={S.synthLabel}>Commentaire</Text>
                                <Text style={[S.synthValue, { flex: 1, textAlign: 'right', marginLeft: 8 }]}>{dossier.commentaire || '—'}</Text>
                            </View>
                        </View>
                    </>
                )}

            </Page>
        </Document>
    );
};

export default DossierPDF;
